import { BulkCheckbox } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { ServiceOrder, ServiceOrderType } from '@mews-commander/data-access';
import { WithBillId, WithServiceId } from '@mews-commander/types';
import { notEmpty } from '@mews-commander/utils';
import RebatedTag, { RebatedTagType } from '../../../../rebate/components/RebatedTag';
import RebateTag from '../../../../rebate/components/RebateTag';
import { TotalAmountAndCount } from '../../../../types';
import { useServiceOrderItemsFlags } from '../useServiceOrderItemsFlags';
import { ORDER_ITEM_CHECKBOX_DATA_TEST_ID } from '../../constants';
import { ProductOrderLabel } from './ProductOrderLabel';
import { SpaceOrderLabel } from './SpaceOrderLabel';

interface ServiceOrderItemLabelProps extends WithBillId, WithServiceId {
    serviceOrder: ServiceOrder;
    serviceOrderId: string;
    allSelected: boolean;
    someSelected: boolean;
    totalAmountAndCount: TotalAmountAndCount;
    toggleSelect(): void;
    areAllServiceOrderItemsRebated: boolean;
    areSomeServiceOrderItemsRebated: boolean;
    areAllServiceOrderItemsRebates: boolean;
    areAllServiceOrderItemsClosed: boolean;
    collectiveRebateIndex?: number;
}

export const ServiceOrderItemLabel: React.FC<ServiceOrderItemLabelProps> = ({
    billId,
    serviceId,
    serviceOrder,
    serviceOrderId,
    someSelected,
    allSelected,
    totalAmountAndCount,
    toggleSelect,
    areAllServiceOrderItemsRebated,
    areSomeServiceOrderItemsRebated,
    areAllServiceOrderItemsRebates,
    areAllServiceOrderItemsClosed,
    collectiveRebateIndex,
}) => {
    const { areAllItemsReinstated } = useServiceOrderItemsFlags({ billId, serviceId, serviceOrderId });

    let text: React.ReactNode | null = null;
    if (notEmpty(serviceOrder)) {
        const { data } = serviceOrder;
        if (data.discriminator === ServiceOrderType.Reservation) {
            text = (
                <SpaceOrderLabel
                    totalAmountAndCount={totalAmountAndCount}
                    serviceId={serviceOrder.serviceId}
                    reservation={data.value}
                />
            );
        } else {
            text = (
                <ProductOrderLabel
                    totalAmountAndCount={totalAmountAndCount}
                    serviceId={serviceOrder.serviceId}
                    serviceOrderId={serviceOrderId}
                />
            );
        }
    }

    const rebatedTagType: RebatedTagType = areAllServiceOrderItemsRebated ? 'fully' : 'partially';

    return (
        <Stack itemAlignment="center" spacing="100" data-service-order-row={serviceOrderId}>
            <BulkCheckbox
                allSelected={allSelected}
                someSelected={someSelected}
                onChange={toggleSelect}
                disabled={areAllServiceOrderItemsRebated && areAllServiceOrderItemsClosed}
                data-test-id={ORDER_ITEM_CHECKBOX_DATA_TEST_ID}
            />
            {areAllServiceOrderItemsRebates && !areAllItemsReinstated ? (
                <RebateTag rebateIndex={collectiveRebateIndex} />
            ) : null}
            {areSomeServiceOrderItemsRebated ? <RebatedTag type={rebatedTagType} /> : null}
            {text}
        </Stack>
    );
};
